import React, { useState, useEffect } from 'react';

const Calculator = () => {
  // Load calculator state from localStorage or use defaults
  const loadCalculatorState = () => {
    try {
      const savedState = localStorage.getItem('webos-calculator-state');
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('Failed to load calculator state:', e);
    }
    return {
      display: '0',
      equation: '',
      prevValue: null,
      operator: null,
      waitingForOperand: false,
      memory: 0
    };
  };

  const initialState = loadCalculatorState();
  
  const [display, setDisplay] = useState(initialState.display);
  const [equation, setEquation] = useState(initialState.equation);
  const [prevValue, setPrevValue] = useState(initialState.prevValue);
  const [operator, setOperator] = useState(initialState.operator);
  const [waitingForOperand, setWaitingForOperand] = useState(initialState.waitingForOperand);
  const [memory, setMemory] = useState(initialState.memory);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      display,
      equation,
      prevValue,
      operator,
      waitingForOperand,
      memory
    };
    localStorage.setItem('webos-calculator-state', JSON.stringify(stateToSave));
  }, [display, equation, prevValue, operator, waitingForOperand, memory]);
  
  // Calculator color theme
  const colors = {
    background: 'rgba(10, 10, 20, 0.2)',
    displayBg: 'rgba(20, 20, 40, 0.4)',
    numberBtn: 'rgba(60, 60, 80, 0.4)',
    numberBtnHover: 'rgba(80, 80, 100, 0.5)',
    operatorBtn: 'rgba(80, 100, 180, 0.4)',
    operatorBtnHover: 'rgba(100, 120, 200, 0.5)',
    equalBtn: 'rgba(120, 90, 200, 0.6)',
    equalBtnHover: 'rgba(140, 110, 220, 0.7)',
    functionBtn: 'rgba(80, 80, 100, 0.3)',
    functionBtnHover: 'rgba(100, 100, 120, 0.4)',
    text: '#fff',
    shadow: '0 4px 10px rgba(0, 0, 0, 0.2)'
  };

  const clearAll = () => {
    setDisplay('0');
    setEquation('');
    setPrevValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const toggleSign = () => {
    const value = parseFloat(display) * -1;
    setDisplay(String(value));
  };

  const inputPercent = () => {
    const value = parseFloat(display) / 100;
    setDisplay(String(value));
    setWaitingForOperand(true);
  };

  const inputDot = () => {
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(String(digit));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(digit) : display + digit);
    }
  };

  const performOperation = (nextOperator) => {
    const inputValue = parseFloat(display);
    
    if (prevValue === null) {
      setPrevValue(inputValue);
    } else if (operator) {
      const currentValue = prevValue || 0;
      let newValue = 0;
      
      switch (operator) {
        case '+':
          newValue = currentValue + inputValue;
          break;
        case '-':
          newValue = currentValue - inputValue;
          break;
        case '×':
          newValue = currentValue * inputValue;
          break;
        case '÷':
          newValue = currentValue / inputValue;
          break;
        default:
          break;
      }
      
      setPrevValue(newValue);
      setDisplay(String(newValue));
      setEquation(`${currentValue} ${operator} ${inputValue} = ${newValue}`);
    }
    
    setWaitingForOperand(true);
    setOperator(nextOperator);
  };

  const handleBackspace = () => {
    if (display.length > 1) {
      setDisplay(display.substring(0, display.length - 1));
    } else {
      setDisplay('0');
    }
  };

  const handleMemoryPlus = () => {
    setMemory(memory + parseFloat(display));
  };

  const handleMemoryMinus = () => {
    setMemory(memory - parseFloat(display));
  };

  const handleMemoryRecall = () => {
    setDisplay(String(memory));
    setWaitingForOperand(true);
  };

  const handleMemoryClear = () => {
    setMemory(0);
  };

  // Button component for consistent styling
  const CalcButton = ({ onClick, label, type = 'number', style = {} }) => {
    const [isHover, setIsHover] = useState(false);
    
    const getBgColor = () => {
      if (isHover) {
        switch (type) {
          case 'operator': return colors.operatorBtnHover;
          case 'equal': return colors.equalBtnHover;
          case 'function': return colors.functionBtnHover;
          default: return colors.numberBtnHover;
        }
      } else {
        switch (type) {
          case 'operator': return colors.operatorBtn;
          case 'equal': return colors.equalBtn;
          case 'function': return colors.functionBtn;
          default: return colors.numberBtn;
        }
      }
    };
    
    return (
      <button 
        onClick={onClick}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        style={{
          backgroundColor: getBgColor(),
          color: colors.text,
          border: 'none',
          borderRadius: '10px',
          fontSize: '18px',
          fontWeight: '500',
          padding: '0',
          margin: '4px',
          cursor: 'pointer',
          height: '48px',
          backdropFilter: 'blur(10px)',
          boxShadow: colors.shadow,
          transition: 'all 0.15s ease',
          ...style
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.background,
      backdropFilter: 'blur(20px)',
      padding: '15px',
      borderRadius: '10px',
      color: colors.text
    }}>
      {/* Display */}
      <div style={{ 
        backgroundColor: colors.displayBg,
        backdropFilter: 'blur(15px)',
        margin: '10px 5px 15px 5px',
        padding: '15px 20px',
        borderRadius: '10px',
        boxShadow: 'inset 0 2px 10px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ 
          fontSize: '14px', 
          color: 'rgba(255, 255, 255, 0.7)',
          height: '20px',
          textAlign: 'right',
          marginBottom: '5px'
        }}>
          {equation}
        </div>
        <div style={{ 
          fontSize: '32px', 
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {display}
        </div>
      </div>
      
      {/* Memory Controls */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        marginBottom: '10px' 
      }}>
        <CalcButton onClick={handleMemoryClear} label="MC" type="function" />
        <CalcButton onClick={handleMemoryRecall} label="MR" type="function" />
        <CalcButton onClick={handleMemoryMinus} label="M-" type="function" />
        <CalcButton onClick={handleMemoryPlus} label="M+" type="function" />
      </div>
      
      {/* Calculator Buttons */}
      <div style={{ 
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(5, 1fr)',
        gap: '2px'
      }}>
        <CalcButton onClick={clearAll} label="AC" type="function" />
        <CalcButton onClick={clearEntry} label="CE" type="function" />
        <CalcButton onClick={handleBackspace} label="⌫" type="function" />
        <CalcButton onClick={() => performOperation('÷')} label="÷" type="operator" />
        
        <CalcButton onClick={() => inputDigit(7)} label="7" />
        <CalcButton onClick={() => inputDigit(8)} label="8" />
        <CalcButton onClick={() => inputDigit(9)} label="9" />
        <CalcButton onClick={() => performOperation('×')} label="×" type="operator" />
        
        <CalcButton onClick={() => inputDigit(4)} label="4" />
        <CalcButton onClick={() => inputDigit(5)} label="5" />
        <CalcButton onClick={() => inputDigit(6)} label="6" />
        <CalcButton onClick={() => performOperation('-')} label="-" type="operator" />
        
        <CalcButton onClick={() => inputDigit(1)} label="1" />
        <CalcButton onClick={() => inputDigit(2)} label="2" />
        <CalcButton onClick={() => inputDigit(3)} label="3" />
        <CalcButton onClick={() => performOperation('+')} label="+" type="operator" />
        
        <CalcButton onClick={toggleSign} label="±" />
        <CalcButton onClick={() => inputDigit(0)} label="0" />
        <CalcButton onClick={inputDot} label="." />
        <CalcButton onClick={() => performOperation('=')} label="=" type="equal" />
      </div>
    </div>
  );
};

export default Calculator; 